import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDom from 'react-dom';
import { connect, useSelector } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import Map from 'components/Map';
import { CFCProps, SCREEN_BREAK_POINT } from 'types';
import MapSettings from 'components/Map/settings';
import MaskingLayer from 'components/Map/maskingLayer';
import { zoomToFeature } from 'components/Map/helper';
import {
  getFeatureServerUrl,
  featureRequestInterceptors
} from 'services/map.service';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import {
  queryFeatureLayerExtentStart,
  queryClusterExtentStart
} from './localStore/actions';
import { useEmptyCallback } from 'utilities/hooks.util';
import PopUp from './popup';
import Utils from 'utilities/helper.util';
import _ from 'lodash';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import { selectTeamBoundaryToggle } from 'redux/team/selectors';
import { selectorAlertNotificationShowHide } from 'redux/session/selectors';

const { modules, pointDarkSymbol, pointSymbol } = MapSettings;

const extraMapModules = [
  modules.featureLayer,
  modules.polygon,
  modules.baseLayerView2D,
  modules.handles,
  modules.projection,
  modules.watchUtils,
  modules.layer,
  modules.tileInfo,
  modules.webMercatorUtils
];

const filterStringReducer = (accumulator: any, currentValue: any) => {
  const [key, value] = currentValue;
  const query = accumulator ? `${accumulator} AND ` : '';

  switch (key) {
    case 'team':
      return value
        ? `${query}project_id = ${value}`
        : `${query}project_id IS NULL`;
    case 'user':
      return value ? `${query}user_id = ${value}` : accumulator;
    case 'status': {
      const withoutAutoRenew = _.without(value, 'auto-renew');
      const status = withoutAutoRenew.includes('waiting')
        ? [...withoutAutoRenew, 'created']
        : withoutAutoRenew;

      const withArchived = value.includes('archived');
      let archivedQuery = withArchived ? '' : 'archived_at IS NULL AND ';
      if (status.length === 1 && status.includes('archived')) {
        archivedQuery = 'archived_at IS NOT NULL ';
      }

      const statusQuery =
        status.length === 1 && status.includes('archived')
          ? ''
          : status.length > 0
          ? `status IN (${status.map((s: string) => `'${s}'`).join(',')})`
          : '';

      /* temp remove auto renew filter
      if (status.length > 0 && value.includes('auto-renew')) {
        return `${query}${statusQuery}`;
      } else {
        if (status.length > 0) {
          return `${query}${statusQuery} AND auto_renew = false`;
        } else {
          return `${query}auto_renew = true`;
        }
      }
      */

      return `${query}${archivedQuery}${statusQuery}`;
    }
    case 'userReference':
      return value
        ? `${query}UPPER(user_reference) LIKE '%${value.toUpperCase()}%'`
        : accumulator;
    case 'jobId':
      return value
        ? `${query}(external_id = ${value} OR user_reference LIKE '%${value}%')`
        : accumulator;
    case 'range':
      return value
        ? `${query}(${value.type} >= TIMESTAMP '${Utils.getNewDate(
            value.date.start
          )
            .utc()
            .format('YYYY-MM-DD HH:hh:ss')}' AND ${
            value.type
          } <= TIMESTAMP '${Utils.getNewDate(value.date.end)
            .utc()
            .format('YYYY-MM-DD HH:hh:ss')}')`
        : accumulator;
    default:
      return accumulator;
  }
};

const setRenderer = (id: string, theme: string) => {
  const highlightBorder = '#3ddbd9';
  const lightBorder = {
    fulfilled: 'rgba(0, 45, 156 ,0.5)',
    expired: 'rgba(160, 160, 160, 0.75)',
    other: 'rgba(170, 125, 242, 0.75)'
  };
  const colors = {
    fulfilled: 'rgba(0, 45, 156, 0.35)',
    expired: 'rgba(141, 141, 141, 0.35)',
    other: 'rgba(190, 149, 255, 0.35)'
  };
  const darkBorders = {
    fulfilled: 'rgba(59, 161, 255, 1)',
    expired: 'rgba(213, 213, 213, 1)',
    other: 'rgba(231, 201, 255, 1)'
  };
  const darkColors = {
    fulfilled: 'rgba(40, 118, 210, 0.48)',
    expired: 'rgba(187, 187, 187, 0.65)',
    other: 'rgba(198, 162, 255, 0.7)'
  };
  const { loadedMap } = window;
  if (loadedMap) {
    const layer = loadedMap.findLayerById('enquiries-feature-layer');
    if (layer) {
      const objectId = id !== '' ? id : '0';
      const renderer = {
        type: 'unique-value',
        valueExpression: `
          var id = $feature.objectid;
          var status = $feature.status;
          When(
            id == ${objectId} && status == "fulfilled", "selected-fulfilled",
            id == ${objectId} && status == "expired", "selected-expired",
            id == ${objectId}, "selected",
            status == "fulfilled", "fulfilled",
            status == "expired", "expired",
            "other"
          );
        `,
        valueExpressionTitle: 'Highlight',
        uniqueValueInfos: [
          {
            value: 'selected-fulfilled',
            symbol: {
              type: 'simple-fill',
              color:
                theme === 'dark'
                  ? darkColors['fulfilled']
                  : colors['fulfilled'],
              style: 'solid',
              outline: {
                color: highlightBorder,
                width: 2
              }
            }
          },
          {
            value: 'selected-expired',
            symbol: {
              type: 'simple-fill',
              color:
                theme === 'dark' ? darkColors['expired'] : colors['expired'],
              style: 'solid',
              outline: {
                color: highlightBorder,
                width: 2
              }
            }
          },
          {
            value: 'selected',
            symbol: {
              type: 'simple-fill',
              color: theme === 'dark' ? darkColors['other'] : colors['other'],
              style: 'solid',
              outline: {
                color: highlightBorder,
                width: 2
              }
            }
          },
          {
            value: 'fulfilled',
            symbol: {
              type: 'simple-fill',
              color:
                theme === 'dark'
                  ? darkColors['fulfilled']
                  : colors['fulfilled'],
              style: 'solid',
              outline:
                theme === 'dark'
                  ? {
                      color: darkBorders['fulfilled'],
                      width: 2
                    }
                  : {
                      color: lightBorder['fulfilled'],
                      width: 2
                    }
            }
          },
          {
            value: 'expired',
            symbol: {
              type: 'simple-fill',
              color:
                theme === 'dark' ? darkColors['expired'] : colors['expired'],
              style: 'solid',
              outline:
                theme === 'dark'
                  ? {
                      color: darkBorders['expired'],
                      width: 2
                    }
                  : {
                      color: lightBorder['expired'],
                      width: 2
                    }
            }
          },
          {
            value: 'other',
            symbol: {
              type: 'simple-fill',
              color: theme === 'dark' ? darkColors['other'] : colors['other'],
              style: 'solid',
              outline:
                theme === 'dark'
                  ? {
                      color: darkBorders['other'],
                      width: 2
                    }
                  : {
                      color: lightBorder['other'],
                      width: 2
                    }
            }
          }
        ]
      };

      layer.renderer = renderer;
    }
  }
};

const setClusterRenderer = (theme: string) => {
  const { loadedMap } = window;
  if (loadedMap) {
    const layer = loadedMap.findLayerById('enquiries-cluster-layer');
    if (layer) {
      const renderer = {
        type: 'simple',
        symbol: theme === 'dark' ? pointDarkSymbol : pointSymbol
      };
      layer.renderer = renderer;
    }
  }
};

const addMaskingLayer = (shape: GeoJSON.Polygon, zoom: boolean) => {
  const { loadedMap, loadedMapModules } = window;
  if (loadedMap && loadedMapModules) {
    const { Graphic, WebMercatorUtils } = loadedMapModules;
    let maskingLayer = loadedMap.findLayerById('masking-layer');
    if (!maskingLayer) {
      maskingLayer = MaskingLayer({
        id: 'masking-layer',
        color: [0, 0, 0, 0.2],
        distance: 1
      });
      loadedMap.add(maskingLayer);
    }

    const geometry = geojsonToArcGIS(shape);
    const webMercatorGeometry =
      WebMercatorUtils.geographicToWebMercator(geometry);
    const graphic = new Graphic({
      geometry: {
        ...webMercatorGeometry,
        type: 'polygon'
      }
    });
    maskingLayer.geometry = graphic.geometry;
    if (zoom) {
      zoomToFeature(graphic.geometry, 0, true);
    }
  }
};

const MapView: React.FC<Props> = ({
  refreshMap,
  user,
  teamId,
  teamShape,
  baseMapTheme,
  selectedId,
  handleSetEnquiryId,
  onOffClusterLayer,
  searchText,
  searchFilter,
  showMyEnquiriesOnly
}) => {
  const showTeamBoundary = useSelector(selectTeamBoundaryToggle);
  const alertNotificationMessage = useSelector(
    selectorAlertNotificationShowHide
  );
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const [filterArr, setFilterArr] = useState<any[]>([]);
  const selectedFeatureExtent = useRef<any | undefined>();
  const isPrivateTeam = useRef<boolean>(true);
  const addedMaskOnce = useRef<boolean>(false);
  const teamChangedAfterAddedMask = useRef<boolean>(false);
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const {
    featureLayerExtent: enquiriesLayerExtent,
    clusterExtent,
    resultCount
  } = localStore;

  const onMapReady = () => {
    const { loadedMap, loadedMapModules, loadedView } = window;
    if (loadedMap) {
      const { Config, FeatureLayer } = loadedMapModules;
      const featureLayerUrl = getFeatureServerUrl('Enquiries');
      const clusterLayerUrl = getFeatureServerUrl('Enquiries_Pts');
      Config.request.interceptors.push(
        {
          urls: featureLayerUrl,
          before: featureRequestInterceptors
        },
        {
          urls: clusterLayerUrl,
          before: featureRequestInterceptors
        }
      );

      const popupTemplate = {
        content: async (feature: any) => {
          const node = document.createElement('div');
          ReactDom.render(
            <PopUp
              attributes={feature?.graphic?.attributes || null}
              selectedFeatureExtent={selectedFeatureExtent}
              extent={feature?.graphic?.geometry?.extent || null}
              onButtonClick={handleSelectObject}
            />,
            node
          );
          return node;
        },
        outFields: '*',
        returnGeometry: true,
        overwriteActions: true
      };

      const featureLayer = new FeatureLayer({
        id: 'enquiries-feature-layer',
        url: featureLayerUrl,
        outFields: ['objectid', 'status'],
        popupTemplate
      });

      const clusterLayer = new FeatureLayer({
        id: 'enquiries-cluster-layer',
        url: clusterLayerUrl,
        outFields: ['objectid'],
        popupTemplate,
        featureReduction: {
          type: 'cluster',
          clusterRadius: '160px',
          clusterMaxSize: '64px',
          clusterMinSize: '48px',
          labelingInfo: [
            {
              labelExpressionInfo: {
                expression: '$feature.cluster_count'
              },
              deconflictionStrategy: 'none',
              labelPlacement: 'center-center',
              symbol: {
                type: 'text',
                color: 'black',
                font: {
                  size: '16px',
                  weight: 'bold'
                }
              }
            }
          ]
        }
      });

      featureLayer.visible = false;

      loadedMap.addMany([featureLayer, clusterLayer]);

      loadedView.popup.visibleElements = {
        featureNavigation: false,
        closeButton: false
      };
      loadedView.popup.actions = null;
      loadedView.popup.dockOptions = {
        buttonEnabled: false,
        breakpoint: {
          width: SCREEN_BREAK_POINT.MD
        },
        position: 'bottom-center'
      };

      const calculatePopupAlignment = () => {
        let popupPos = 'auto';

        const popupRectBoundary =
          loadedView.popup.domNode.getBoundingClientRect();
        const finalPopupXPos = popupRectBoundary.x + popupRectBoundary.width;

        const popupY = loadedView.popup?.location?.y;
        const mapY = loadedView.center?.y;
        const popupX = loadedView.popup?.location?.x;
        const mapX = loadedView.center?.x;

        const minX: number = loadedView.position[0];
        const maxX: number = loadedView.size[0];

        if (popupY && mapY) {
          popupPos = popupY > mapY ? 'bottom-center' : 'top-center';
        }

        if (popupPos === 'bottom-center') {
          popupPos =
            popupX < mapX
              ? popupRectBoundary.x < minX
                ? 'bottom-right'
                : 'bottom-center'
              : finalPopupXPos > maxX
              ? 'bottom-left'
              : 'bottom-center';
        }

        if (popupPos === 'top-center') {
          popupPos =
            popupX < mapX
              ? popupRectBoundary.x < minX
                ? 'top-right'
                : 'auto'
              : 'auto';
        }

        return popupPos;
      };

      loadedView.popup.alignment = () => calculatePopupAlignment();

      loadedView.watch('zoom', () => {
        onOffClusterLayer();
      });
    }
    setIsMapLoaded(true);
  };

  const onMapClick = (results: any[]) => {
    const { loadedView, loadedMap } = window;
    const clusterLayerView = loadedView.layerViews.items.find(
      (lv: any) => lv?.layer?.id === 'enquiries-cluster-layer'
    );
    const cluster = results.find(
      (r) => r?.graphic?.layer?.id === 'enquiries-cluster-layer'
    );
    const featureLayer = loadedMap.findLayerById('enquiries-feature-layer');
    if (cluster) {
      if (!cluster.graphic.isAggregate) {
        queryClusterExtent(featureLayer, cluster.graphic.getObjectId(), false);
      } else {
        queryClusterExtent(clusterLayerView, cluster.graphic.getObjectId());
      }
    }
  };

  const handleSelectObject = (
    id: number,
    isDataRotated = false,
    isLimitedReached = false
  ) => {
    const { loadedView } = window;
    loadedView.popup.close();
    handleSetEnquiryId(id, isDataRotated, isLimitedReached);
  };

  const queryEnquiriesExtent = useEmptyCallback((featureLayer: any) => {
    localDispatch(queryFeatureLayerExtentStart(featureLayer));
  });

  const queryClusterExtent = useEmptyCallback(
    (layerView: any, id: number, isAggregate = true) => {
      localDispatch(queryClusterExtentStart(layerView, id, isAggregate));
    }
  );

  const setDefinitionExpression = useCallback(
    (filterArr: any[]) => {
      const { loadedMap } = window;
      if (loadedMap) {
        const featureLayer = loadedMap.findLayerById('enquiries-feature-layer');
        const clusterLayer = loadedMap.findLayerById('enquiries-cluster-layer');
        const definitionExpression = filterArr.reduce(filterStringReducer, '');

        if (featureLayer) {
          featureLayer.definitionExpression = definitionExpression;
          queryEnquiriesExtent(featureLayer);
        }

        if (clusterLayer) {
          clusterLayer.definitionExpression = definitionExpression;
        }
      }
    },
    [queryEnquiriesExtent]
  );

  useEffect(() => {
    const finalFilterArr = refreshMap ? [['user', '0']] : filterArr;
    isMapLoaded &&
      filterArr.length > 0 &&
      setDefinitionExpression(finalFilterArr);
  }, [isMapLoaded, setDefinitionExpression, filterArr, refreshMap]);

  useEffect(() => {
    if (enquiriesLayerExtent && typeof enquiriesLayerExtent !== 'string') {
      if (searchText && resultCount && resultCount <= 2) {
        zoomToFeature(enquiriesLayerExtent, 0, true);
      } else if (isPrivateTeam.current && !window.lastVisitedExtent) {
        if (!enquiriesLayerExtent?.noRecord) {
          zoomToFeature(enquiriesLayerExtent, 0, true);
        } else {
          if (window.homeExtent) {
            zoomToFeature(window.homeExtent);
          }
        }
      }
    }
  }, [enquiriesLayerExtent, resultCount, searchText]);

  useEffect(() => {
    if (clusterExtent) {
      if (clusterExtent.isAggregate) {
        zoomToFeature(clusterExtent.extent, 0, true);
      } else {
        selectedFeatureExtent.current = clusterExtent.extent;
      }
    }
  }, [clusterExtent]);

  useEffect(() => {
    const text = String(searchText || searchFilter.userReference).trim();
    const isJobId = text.match(/^\d{7,9}$/);
    let userId = undefined;
    if (showMyEnquiriesOnly) {
      userId = user.id;
    } else if (searchFilter.enquirer && teamId) {
      userId = searchFilter.enquirer;
    }
    const filter = isJobId
      ? {
          jobId: text
        }
      : {
          userReference: text,
          user: userId,
          team: teamId,
          status: searchFilter.status,
          range: searchFilter.range
        };
    const filterArr = Object.entries({
      ...filter
    });
    setFilterArr(filterArr);
    isPrivateTeam.current = teamId === undefined;
  }, [teamId, searchText, showMyEnquiriesOnly, searchFilter, user]);

  useEffect(() => {
    isMapLoaded && baseMapTheme && setRenderer(selectedId, baseMapTheme);
  }, [isMapLoaded, selectedId, baseMapTheme]);

  useEffect(() => {
    isMapLoaded && baseMapTheme && setClusterRenderer(baseMapTheme);
  }, [isMapLoaded, baseMapTheme]);

  useEffect(() => {
    if (addedMaskOnce.current) {
      teamChangedAfterAddedMask.current = true;
    }
  }, [teamId]);

  useEffect(() => {
    if (isMapLoaded) {
      const { loadedMap } = window;
      if (teamShape && showTeamBoundary) {
        addMaskingLayer(
          teamShape,
          teamChangedAfterAddedMask.current || !window.lastVisitedExtent
        );
      } else {
        const maskingLayer = loadedMap.findLayerById('masking-layer');
        if (maskingLayer) {
          loadedMap.remove(maskingLayer);
        }
      }
      addedMaskOnce.current = true;
    }
  }, [isMapLoaded, teamShape, showTeamBoundary, searchText]);

  return (
    <div
      className={`home-map--view ${
        alertNotificationMessage ? 'noti-alert-map' : ''
      }`}
      data-testid="homeMapView"
    >
      <Map
        extraModules={extraMapModules}
        onReady={onMapReady}
        onSelect={onMapClick}
        showHomeButton={true}
        showSearchButton={true}
        popUpEnabledLayers={[
          'enquiries-cluster-layer',
          'enquiries-feature-layer'
        ]}
        showMapStyleButton={true}
      />
    </div>
  );
};

const mapStateToProps = ({ teamCache, session }: RootState) => ({
  baseMapTheme: session.baseMap?.theme || 'light',
  teamId: teamCache.currentTeamId,
  user: session.currentUser,
  teamShape: teamCache.currentTeamShape
});

const connector = connect(mapStateToProps);

type OwnProps = {
  handleSetEnquiryId: Function;
  searchText: string;
  searchFilter: any;
  showMyEnquiriesOnly: boolean;
  selectedId: string;
  onOffClusterLayer: Function;
  refreshMap: boolean;
};

type Props = CFCProps<typeof connector> & OwnProps;

MapView.displayName = 'HomeMapView';

export default connector(MapView);
